import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeaders } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ChartBar,
  CurrencyEur,
  Truck,
  Users,
  Tire,
  Spinner
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  en_attente: 'En attente',
  validee: 'Validée',
  en_route: 'En route',
  en_cours: 'En cours',
  terminee: 'Terminée',
  encaissee: 'Encaissée',
};

const STATUS_COLORS = {
  en_attente: 'bg-amber-100 text-amber-700',
  validee: 'bg-blue-100 text-blue-700',
  en_route: 'bg-cyan-100 text-cyan-700',
  en_cours: 'bg-purple-100 text-purple-700',
  terminee: 'bg-green-100 text-green-700',
  encaissee: 'bg-reco-100 text-reco-700',
};

const PERIOD_LABELS = { '7d': '7 jours', '30d': '30 jours', '90d': '90 jours', 'all': 'Tout' };

export default function ReportsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');

  useEffect(() => {
    const fetchReports = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/api/stats/reports?period=${period}`, {
          headers: getAuthHeaders()
        });
        setData(response.data);
      } catch (err) {
        toast.error('Erreur lors du chargement des rapports');
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, [period]);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="text-gray-500 -ml-2 gap-2"
                data-testid="reports-back-button"
              >
                <ArrowLeft size={18} />
                Retour
              </Button>
              <div className="h-6 w-px bg-gray-300" />
              <ChartBar size={22} className="text-reco-500" />
              <h1 className="text-lg font-heading font-bold text-gray-900">Rapports</h1>
            </div>

            <div className="flex gap-1 bg-gray-100 rounded-full p-1 border border-gray-200">
              {Object.entries(PERIOD_LABELS).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setPeriod(key)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${
                    period === key
                      ? 'bg-reco-500 text-white'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid={`period-${key}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Spinner size={32} className="animate-spin text-reco-500" />
          </div>
        ) : data ? (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="reports-summary">
              <SummaryCard
                icon={<Truck size={20} />}
                label="Interventions"
                value={data.summary.total_interventions}
                color="text-reco-500"
              />
              <SummaryCard
                icon={<CurrencyEur size={20} />}
                label="CA encaissé"
                value={`${data.summary.total_ca.toLocaleString('fr-FR')} €`}
                color="text-green-600"
              />
              <SummaryCard
                icon={<CurrencyEur size={20} />}
                label="Prix moyen"
                value={`${data.summary.avg_price.toLocaleString('fr-FR')} €`}
                color="text-blue-600"
              />
              <SummaryCard
                icon={<Truck size={20} />}
                label="Encaissées"
                value={data.summary.total_encaissee}
                color="text-reco-600"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* CA by Day Chart */}
              {data.ca_by_day.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="ca-by-day-chart">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <CurrencyEur size={16} />
                    CA par jour
                  </h3>
                  <BarChart data={data.ca_by_day} valueKey="ca" labelKey="date" suffix=" €" />
                </div>
              )}

              {/* By Status */}
              <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="by-status">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <ChartBar size={16} />
                  Par statut
                </h3>
                <div className="space-y-3">
                  {data.by_status.map((item) => (
                    <div key={item.status} className="flex items-center justify-between">
                      <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${STATUS_COLORS[item.status] || 'bg-gray-100 text-gray-600'}`}>
                        {STATUS_LABELS[item.status] || item.status}
                      </span>
                      <div className="flex items-center gap-3 flex-1 ml-4">
                        <div className="flex-1 bg-gray-100 rounded-full h-2.5">
                          <div
                            className="bg-reco-400 h-2.5 rounded-full transition-all"
                            style={{ width: `${Math.max(4, (item.count / data.summary.total_interventions) * 100)}%` }}
                          />
                        </div>
                        <span className="text-sm font-semibold text-gray-700 w-8 text-right">{item.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* By Technician */}
              {data.by_technician.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="by-technician">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Users size={16} />
                    Par technicien
                  </h3>
                  <div className="space-y-4">
                    {data.by_technician.map((tech) => (
                      <div key={tech.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                        <div>
                          <div className="font-medium text-gray-900">{tech.name}</div>
                          <div className="text-xs text-gray-500">{tech.count} intervention{tech.count > 1 ? 's' : ''}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-reco-600">{tech.ca.toLocaleString('fr-FR')} €</div>
                          <div className="text-xs text-gray-400">CA encaissé</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* By Type */}
              {data.by_type.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="by-type">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Truck size={16} />
                    Par type d'intervention
                  </h3>
                  <div className="space-y-4">
                    {data.by_type.map((item) => (
                      <div key={item.type} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                        <div>
                          <div className="font-medium text-gray-900">{item.type}</div>
                          <div className="text-xs text-gray-500">{item.count} intervention{item.count > 1 ? 's' : ''}</div>
                        </div>
                        <div className="font-bold text-gray-700">{item.ca.toLocaleString('fr-FR')} €</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Tire Dimensions */}
              {data.by_dimension.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="by-dimension">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Tire size={16} />
                    Dimensions les plus demandées
                  </h3>
                  <div className="space-y-2">
                    {data.by_dimension.map((item, idx) => (
                      <div key={item.dimension} className="flex items-center gap-3">
                        <span className="text-xs font-bold text-gray-400 w-5">{idx + 1}.</span>
                        <span className="font-mono text-sm font-medium text-gray-900 flex-1">{item.dimension}</span>
                        <span className="text-sm text-gray-500">{item.count}x</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-20 text-gray-500">Aucune donnée disponible</div>
        )}
      </main>
    </div>
  );
}

function SummaryCard({ icon, label, value, color }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
      <div className={`${color} mb-2`}>{icon}</div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      <div className="text-sm text-gray-500">{label}</div>
    </div>
  );
}

function BarChart({ data, valueKey, labelKey, suffix = '' }) {
  if (!data.length) return null;
  const maxVal = Math.max(...data.map(d => d[valueKey]));

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return `${d.getDate()}/${d.getMonth() + 1}`;
  };

  return (
    <div className="flex items-end gap-1.5 h-40">
      {data.map((item, idx) => {
        const height = maxVal > 0 ? (item[valueKey] / maxVal) * 100 : 0;
        return (
          <div key={idx} className="flex-1 flex flex-col items-center gap-1 min-w-0">
            <span className="text-[10px] text-gray-500 truncate w-full text-center">
              {item[valueKey] > 0 ? `${Math.round(item[valueKey])}${suffix}` : ''}
            </span>
            <div
              className="w-full bg-reco-400 rounded-t-sm transition-all hover:bg-reco-500"
              style={{ height: `${Math.max(2, height)}%` }}
              title={`${formatDate(item[labelKey])}: ${item[valueKey]}${suffix}`}
            />
            <span className="text-[9px] text-gray-400 truncate w-full text-center">
              {formatDate(item[labelKey])}
            </span>
          </div>
        );
      })}
    </div>
  );
}
